import { FC } from "react";
import { Button, Modal } from "@monolith-forensics/monolith-ui";
import ButtonMenu from "../../../components/Forms/components/ButtonMenu.js";
import { Evidence } from "@/Features";
import EvidenceAPI from "@/api/evidence";
import { useSnackbar, VariantType } from "notistack";
import { usePermissions } from "@/hooks/usePermissions";
import LoggingAPI from "@/api/logging";
import { useFeatures } from "@/contexts/FeaturesContext";
import { evidenceDeleteFilesMessage } from "@/Features/EvidenceFiles/evidence-files.utils";

interface DeleteEvidenceModalProps {
  open: boolean;
  onClose: () => void;
  onDelete: (deletedEvidence: Evidence) => void;
  evidenceInfo: Evidence | null;
}

export const DeleteEvidenceModal: FC<DeleteEvidenceModalProps> = ({
  open,
  onClose,
  onDelete,
  evidenceInfo,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser } = usePermissions();
  const { hasFeatures, FEATURES } = useFeatures();
  // What goes with the item (ENG-2133): every file in its Evidence Files
  // folder, counted by file_count (images or not; photo_count is images only).
  // An API older than ENG-2247 sends no file_count, but its photo_count still
  // counts every file, so it's the fallback.
  const filesMessage = hasFeatures([FEATURES.EVIDENCE_FILES_IN_CASE_FILES])
    ? evidenceDeleteFilesMessage(
        evidenceInfo?.file_count ?? evidenceInfo?.photo_count
      )
    : null;

  const handleDelete = async () => {
    try {
      if (evidenceInfo?.evidence_id) {
        await EvidenceAPI.deleteEvidence({
          evidence_id: evidenceInfo.evidence_id,
        });

        onDelete?.(evidenceInfo);

        enqueueSnackbar(`Evidence Item Deleted.`, {
          variant: "success" as VariantType,
        });

        LoggingAPI.createLog({
          case_id: parseInt(evidenceInfo.case_id.toString()),
          user_id: currentUser.user_id,
          message: `Deleted evidence item: ${evidenceInfo.evidence_number}`,
          event: "evidence:delete",
          object_type: "evidence",
          object_uuid: evidenceInfo.uuid,
        });
      }
      onClose();
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        `An error occurred while deleting evidence item ${
          evidenceInfo?.evidence_number || ""
        }. Please try again.`,
        { variant: "error" as VariantType }
      );
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Modal.Title>Delete Evidence Item</Modal.Title>
      <div style={{ fontWeight: 500, margin: "20px 0px" }}>
        Are you sure you want to delete evidence item{" "}
        <span style={{ fontWeight: 800 }}>
          "{evidenceInfo?.evidence_number}"
        </span>
        ?
        {filesMessage && (
          <p data-testid="delete-evidence-files-message">{filesMessage}</p>
        )}
        <p>This action cannot be undone.</p>
      </div>
      <ButtonMenu>
        <Button variant="subtle" size="xs" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          size="xs"
          onClick={handleDelete}
        >
          Delete Evidence
        </Button>
      </ButtonMenu>
    </Modal>
  );
};
